import type { SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto } from "../../api";
import type { PlacementHistoryEntry } from "./usePlacementHistory";

type Props = {
	entries: PlacementHistoryEntry[];
	els: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	field: "tower" | "chamber";
	isLoading?: boolean;
	isError?: boolean;
};

function resolveLocationLabel(
	entry: PlacementHistoryEntry,
	els: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[],
): string {
	const el = els.find((e) => e.id === entry.experimentalLocationId);
	if (el) {
		return el.friendlyName ?? (el.plotId != null ? `Plot ${el.plotId}` : `EL ${entry.experimentalLocationId}`);
	}
	return `EL ${entry.experimentalLocationId}`;
}

function describeHeight(entry: PlacementHistoryEntry, field: "tower" | "chamber"): string | null {
	const height = field === "tower" ? entry.towerHeightInMeters : entry.chamberHeightInMeters;
	if (height == null) {
		return null;
	}
	let text = `${height.toFixed(2)} m`;
	if (entry.chamberExtensionInMeters != null && entry.chamberExtensionInMeters > 0) {
		text += ` +${entry.chamberExtensionInMeters.toFixed(2)}m extension (${entry.chamberExtensionIsOpaque ? "opaque" : "clear"})`;
	}
	return text;
}

function dedupeByPosition(entries: PlacementHistoryEntry[]): PlacementHistoryEntry[] {
	const seen = new Map<string, PlacementHistoryEntry>();
	for (const entry of entries) {
		const key = [
			entry.experimentalLocationId,
			entry.validFrom,
			entry.towerHeightInMeters,
			entry.chamberHeightInMeters,
			entry.chamberExtensionInMeters,
			entry.chamberExtensionIsOpaque,
		].join("|");
		const existing = seen.get(key);
		if (!existing || entry.validTo == null || (existing.validTo != null && entry.validTo > existing.validTo)) {
			seen.set(key, entry);
		}
	}
	return Array.from(seen.values());
}

export function PlacementHistoryTimeline({
	entries,
	els,
	field,
	isLoading,
	isError,
}: Props) {
	const sorted = dedupeByPosition(entries).sort((a, b) => {
		const aTime = a.validFrom ? new Date(a.validFrom).getTime() : -Infinity;
		const bTime = b.validFrom ? new Date(b.validFrom).getTime() : -Infinity;
		return bTime - aTime;
	});

	return (
		<div className="text-sm mt-2 space-y-2">
			{isLoading && <p className="text-xs text-gray-400">Loading…</p>}
			{isError && (
				<p className="text-xs text-red-500">Failed to load history</p>
			)}
			{!isLoading && !isError && sorted.length === 0 && (
				<p className="text-xs text-gray-400">No placement history</p>
			)}
			{!isLoading && !isError && sorted.length > 0 && (
				<ul className="space-y-1.5">
					{sorted.map((entry, i) => (
						<li
							key={`${entry.sensorId}-${entry.experimentalLocationId}-${entry.validFrom}-${i}`}
							className="border-l-2 border-gray-200 pl-2 py-0.5"
						>
							<div className="flex items-center gap-1.5 text-xs flex-wrap">
								<span className="font-medium text-gray-700">
									{entry.scope}
								</span>
								<span className="text-gray-500">
									{resolveLocationLabel(entry, els)}
								</span>
								{describeHeight(entry, field) && (
									<span className="text-gray-600">{describeHeight(entry, field)}</span>
								)}
							</div>
							<div className="text-xs text-gray-400">
								{entry.validFrom ? entry.validFrom.slice(0, 10) : "unknown"} –{" "}
								{entry.validTo ? entry.validTo.slice(0, 10) : "present"}
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
